import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { OrderI } from "../utils/types";
import blackStar from "../assets/images/icons/blackstar.png";
import filledStar from "../assets/images/icons/star.png";
import download from "../assets/images/order/download.png";
import { orderService } from "../services";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import ItemsAddedModal from "../modal/ItemsAddedModal";
import FoodTypeIcon from "./FoodTypeIcon";
import defaultImage from "../assets/images/cuisines/default.jpeg";
import { getOrderRestaurantRating, getOrderDeliveryRating, extractId } from "../utils/helpers";
import RatingModal from "../modal/RatingModal";

const PreviousOrders = ({
  orders,
  onRated,
}: {
  orders: OrderI[];
  onRated?: (orderId: string, rating: number) => void;
}) => {
  const navigate = useNavigate();
  const { addToCart, clearCart, getCartRestaurantId } = useCart();
  const [restaurantRating, setRestaurantRating] = useState<number>(0);
  const [deliveryRating, setDeliveryRating] = useState<number>(0);
  const [submittingRestaurant, setSubmittingRestaurant] = useState(false);
  const [ratingModal, setRatingModal] = useState<"restaurant" | "delivery" | null>(
    null
  );
  const [modalRating, setModalRating] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [showItemsAdded, setShowItemsAdded] = useState(false);

  const order = orders[0];

  useEffect(() => {
    if (!order) {
      setRestaurantRating(0);
      return;
    }
    setRestaurantRating(getOrderRestaurantRating(order));
    setDeliveryRating(getOrderDeliveryRating(order));
  }, [order]);

  if (!order) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">No previous orders</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const locationLabel =
    [
      order.restaurant?.location?.city,
      order.restaurant?.location?.state,
    ]
      .filter(Boolean)
      .join(", ") ||
    order.restaurant?.location?.address ||
    "Address not available";

  const openRestaurantRating = (rating: number) => {
    if (restaurantRating > 0) return;
    setModalRating(rating);
    setRatingModal("restaurant");
  };

  const openDeliveryRating = (rating: number) => {
    if (deliveryRating > 0) return;
    setModalRating(rating);
    setRatingModal("delivery");
  };

  const handleSubmitRating = async (rating: number, review: string) => {
    if (!order) return;
    if (ratingModal === "restaurant") {
      const restaurantId =
        extractId(order.restaurantId) || extractId(order.restaurant?._id);
      if (!restaurantId) {
        toast.error("Restaurant not found");
        throw new Error("Restaurant not found");
      }
      setSubmittingRestaurant(true);
      try {
        await orderService.rateRestaurant({
          orderId: order._id,
          restaurantId,
          rating,
          review,
        });
        setRestaurantRating(rating);
        onRated?.(order._id, rating);
        toast.success("Thank you for your rating!");
      } finally {
        setSubmittingRestaurant(false);
      }
      return;
    }

    const deliveryAgentId =
      extractId(order.deliveryAgent?._id) || extractId(order.deliveryAgentId);
    if (!deliveryAgentId) {
      toast.error("No delivery agent found");
      throw new Error("No delivery agent found");
    }
    await orderService.rateDeliveryAgent({
      orderId: order._id,
      deliveryAgentId,
      rating,
      review,
    });
    setDeliveryRating(rating);
    toast.success("Thank you for your rating!");
  };

  const handleDownloadInvoice = async () => {
    setDownloading(true);
    try {
      const blob = await orderService.downloadInvoice(order._id);
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${order._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error: any) {
      toast.error(error?.message || "Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  const handleReorder = async () => {
    if (!order.items?.length) {
      toast.error("No items to reorder");
      return;
    }
    setReordering(true);
    try {
      const restaurantId = order.restaurantId || order.restaurant?._id;
      if (!restaurantId) {
        toast.error("Restaurant not found for reorder");
        return;
      }
      const cartRestaurantId = getCartRestaurantId();
      if (cartRestaurantId && cartRestaurantId !== restaurantId) {
        await clearCart();
      }
      for (const item of order.items) {
        const dishId = item.dishId || (item as any).dish?._id;
        if (!dishId) continue;
        await addToCart(dishId, restaurantId, item.quantity || 1, {
          variantId: (item as any).variantId ?? null,
          addons: (item as any).addons || [],
        });
      }
      setShowItemsAdded(true);
    } catch (error: any) {
      toast.error(error?.message || "Failed to reorder");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="px-4 md:px-12 py-6">
      <div className="rounded-2xl border border-[#E8E8E8] dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="flex gap-3 items-center pb-3 border-b border-[#EFEFEF] dark:border-gray-700">
          <img
            src={order.restaurant?.kitchenPhotos?.[0] || defaultImage}
            alt={order.restaurant?.businessName || "restaurant"}
            className="h-14 w-14 rounded-xl object-cover"
          />
          <div>
            <p className="text-[#050505] dark:text-white font-urbanist text-lg font-semibold">
              {order.restaurant?.businessName || "Restaurant"}
            </p>
            <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-sm">
              {locationLabel}
            </p>
          </div>
        </div>

        <div className="py-3 border-b border-dashed border-[#D0D0D0] dark:border-gray-600 space-y-2">
          {order.items?.map((item) => (
            <div key={item._id} className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FoodTypeIcon
                  foodType={item.foodType || item.dish?.foodType}
                  size={14}
                />
                <p className="text-[#050505] dark:text-white font-urbanist text-sm font-medium">
                  {item.quantity} x {item.dishName}
                  {item.variantName && (
                    <span className="text-[#6A6A6A] text-xs">
                      {" "}
                      ({item.variantName})
                    </span>
                  )}
                </p>
              </div>
              <p className="text-[#050505] dark:text-white text-sm">
                ₹{item.totalItemPrice}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-start py-3 border-b border-[#EFEFEF] dark:border-gray-700">
          <div>
            <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs">
              Order Placed on {formatDate(order.createdAt)}
            </p>
            <p className="text-[#8A8A8A] font-urbanist text-xs mt-1 lowercase">
              {order.orderStatus}
            </p>
          </div>
          <p className="text-[#050505] dark:text-white font-urbanist text-base font-semibold">
            ₹{Number(order.totalAmount).toFixed(2)}
          </p>
        </div>

        <div className="flex justify-between items-center pt-3 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[#050505] dark:text-white font-urbanist text-sm font-medium">
              {restaurantRating > 0 ? "You rated" : "Rate Now"}
            </span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  disabled={submittingRestaurant || restaurantRating > 0}
                  onClick={() => openRestaurantRating(index + 1)}
                >
                  <img
                    src={index < restaurantRating ? filledStar : blackStar}
                    alt=""
                    className="h-4 w-4"
                  />
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleReorder}
            disabled={reordering}
            className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#D0D0D0] bg-[#F7F7F7] dark:bg-gray-700 dark:border-gray-600 text-xs font-semibold disabled:opacity-50"
          >
            {reordering ? "Adding..." : "Re-Order"}
          </button>
        </div>

        {order.deliveryAgent && (
          <div className="flex justify-between items-center pt-3 mt-3 border-t border-[#EFEFEF] dark:border-gray-700">
            <div>
              <p className="text-[#050505] dark:text-white font-urbanist text-sm font-semibold">
                {order.deliveryAgent.fullName}
              </p>
              <p className="text-[#8A8A8A] font-urbanist text-xs">
                Delivery Partner
              </p>
            </div>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  disabled={deliveryRating > 0}
                  onClick={() => openDeliveryRating(index + 1)}
                >
                  <img
                    src={index < deliveryRating ? filledStar : blackStar}
                    alt=""
                    className="h-4 w-4"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        <button
          onClick={handleDownloadInvoice}
          disabled={downloading}
          className="flex w-full max-w-[382px] justify-center items-center p-3 gap-2 bg-[#7604A5] hover:bg-[#5a0383] rounded-full text-white disabled:opacity-50"
        >
          <p>{downloading ? "Downloading..." : "Download Invoice"}</p>
          <img src={download} alt="" className="h-5" />
        </button>
      </div>

      {showItemsAdded && (
        <ItemsAddedModal
          onClose={() => setShowItemsAdded(false)}
          onViewCart={() => {
            setShowItemsAdded(false);
            navigate("/order-summary");
          }}
          onContinueShopping={() => {
            setShowItemsAdded(false);
            navigate("/");
          }}
        />
      )}
      {ratingModal && (
        <RatingModal
          onClose={() => setRatingModal(null)}
          orderId={order._id}
          type={ratingModal === "restaurant" ? "restaurant" : "delivery-agent"}
          restaurantId={extractId(order.restaurantId) || extractId(order.restaurant?._id)}
          restaurantName={order.restaurant?.businessName}
          deliveryAgentId={
            extractId(order.deliveryAgent?._id) || extractId(order.deliveryAgentId)
          }
          deliveryAgentName={order.deliveryAgent?.fullName}
          initialRating={modalRating}
          onSubmit={handleSubmitRating}
        />
      )}
    </div>
  );
};

export default PreviousOrders;
